/**
 * Autosave for one open plan (TD §10, FD §3.3).
 *
 * - `notifyChange()` after every committed edit; the doc is saved 2 s after the last change with
 *   `PUT {doc, baseRev}`. While unsaved, a copy is kept in IndexedDB under the plan id.
 * - When the server can't be reached it retries with backoff (2 s … 30 s) and the status says
 *   "Offline, will retry"; coming back online retries at once.
 * - A version is kept automatically after every 15 minutes of active editing, and when the plan is
 *   closed (leaving the page via `close()`, or the tab closing via `pagehide`).
 * - `overwrote: true` from the server is surfaced in the status so the UI can say so.
 * - The card picture is refreshed after a save at most once a minute when `getThumbnail` is given.
 *
 * Opening a plan: call `findUnsavedCopy` first and offer "Restore unsaved changes?" when it
 * returns one (see PlanFrame in ui/shell).
 */
import { useSyncExternalStore } from 'react';
import * as api from '../api';
import type { CreateVersionRequest, PlanDoc, PlanId, PlanMeta, SavePlanResponse, VersionMeta } from '../../shared/types';

// ---- Status ----------------------------------------------------------------------------------

/**
 * - `saved`: everything is on the server.
 * - `pending`: there are changes waiting for the 2 s pause.
 * - `saving`: a save is on its way.
 * - `offline`: the server can't be reached; it will retry by itself.
 * - `signedOut`: the sign-in ran out; changes are kept on this computer.
 * - `error`: the server refused the save (see `message`); changes are kept on this computer.
 */
export type SaveState = 'saved' | 'pending' | 'saving' | 'offline' | 'signedOut' | 'error';

export interface AutosaveStatus {
  state: SaveState;
  /** When the last save reached the server (ms since epoch), or null if none yet this session. */
  lastSavedAt: number | null;
  /** Plain-English problem for `error` and `offline`. */
  message: string | null;
  /** When the next automatic retry happens (ms since epoch), while `offline`. */
  retryAt: number | null;
  /**
   * Set when a save replaced changes someone else had saved in between. Their state was kept in
   * History. Cleared by `acknowledgeOverwrite()`.
   */
  overwrote: { at: number } | null;
}

/** A readable, subscribable value (works with `useSyncExternalStore`). */
export interface Readable<T> {
  get(): T;
  subscribe(listener: () => void): () => void;
}

function createReadable<T>(initial: T): Readable<T> & { set(next: T): void } {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    subscribe(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    set(next) {
      value = next;
      for (const l of listeners) l();
    },
  };
}

/** The words the save indicator shows for a status. */
export function statusLabel(s: AutosaveStatus): string {
  switch (s.state) {
    case 'saved':
      return 'Saved';
    case 'pending':
    case 'saving':
      return 'Saving…';
    case 'offline':
      return 'Offline, will retry';
    case 'signedOut':
      return 'Not saved: please sign in again';
    case 'error':
      return 'Not saved';
  }
}

// ---- Local copies (IndexedDB) ----------------------------------------------------------------

/** The unsaved state of a plan kept on this computer. */
export interface LocalCopy {
  planId: PlanId;
  doc: PlanDoc;
  /** The server rev the edits started from. */
  baseRev: number;
  /** When the last edit in it was made (ms since epoch). */
  editedAt: number;
}

/** Where unsaved copies are kept. */
export interface LocalCopyStore {
  get(planId: PlanId): Promise<LocalCopy | undefined>;
  put(copy: LocalCopy): Promise<void>;
  delete(planId: PlanId): Promise<void>;
}

/** An in-memory store (tests, and browsers without IndexedDB). */
export function memoryStore(): LocalCopyStore {
  const m = new Map<PlanId, LocalCopy>();
  return {
    get: async (id) => m.get(id),
    put: async (c) => void m.set(c.planId, structuredClone(c)),
    delete: async (id) => void m.delete(id),
  };
}

const DB_NAME = 'zoo-planner';
const DB_STORE = 'unsaved';

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** The IndexedDB store; falls back to memory when IndexedDB isn't available (private windows). */
export function indexedDbStore(): LocalCopyStore {
  if (typeof indexedDB === 'undefined') return memoryStore();
  let dbp: Promise<IDBDatabase> | null = null;
  const fallback = memoryStore();
  const open = () => {
    dbp ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE, { keyPath: 'planId' });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbp;
  };
  const run = async <T,>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    return promisify(fn(db.transaction(DB_STORE, mode).objectStore(DB_STORE)));
  };
  return {
    get: (id) => run('readonly', (s) => s.get(id) as IDBRequest<LocalCopy | undefined>).catch(() => fallback.get(id)),
    put: (c) => run('readwrite', (s) => s.put(c)).then(() => undefined, () => fallback.put(c)),
    delete: (id) => run('readwrite', (s) => s.delete(id)).then(() => undefined, () => fallback.delete(id)),
  };
}

let shared: LocalCopyStore | null = null;
/** The store the app uses (one IndexedDB connection for the page). */
export function defaultLocalStore(): LocalCopyStore {
  shared ??= indexedDbStore();
  return shared;
}

/** An unsaved copy found when opening a plan. */
export interface UnsavedCopy extends LocalCopy {
  /** True when someone saved the plan after these edits started; restoring overwrites that (it stays in History). */
  serverChangedSince: boolean;
}

/**
 * Looks for unsaved changes of this plan kept on this computer. Returns null when there are none,
 * or when the copy is the same as what the server has (then it is removed quietly).
 */
export async function findUnsavedCopy(
  meta: PlanMeta,
  serverDoc: PlanDoc,
  store: LocalCopyStore = defaultLocalStore(),
): Promise<UnsavedCopy | null> {
  let copy: LocalCopy | undefined;
  try {
    copy = await store.get(meta.id);
  } catch {
    return null;
  }
  if (!copy) return null;
  // The server forces doc.name = meta.name, so compare without the name.
  if (sameDoc({ ...copy.doc, name: '' }, { ...serverDoc, name: '' })) {
    await store.delete(meta.id).catch(() => undefined);
    return null;
  }
  return { ...copy, serverChangedSince: meta.rev > copy.baseRev };
}

/** Throws away the unsaved copy of a plan ("Discard" in the restore prompt). */
export function discardUnsavedCopy(planId: PlanId, store: LocalCopyStore = defaultLocalStore()): Promise<void> {
  return store.delete(planId).catch(() => undefined);
}

function sameDoc(a: PlanDoc, b: PlanDoc): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ---- Autosave --------------------------------------------------------------------------------

export interface Autosave {
  /** Call after every committed edit (not during a drag). */
  notifyChange(): void;
  /** Saves now if anything is unsaved and resolves once it's on the server; rejects with an ApiFailure. */
  flush(): Promise<void>;
  /** The save indicator state. */
  status: Readable<AutosaveStatus>;
  /** The server rev the current doc is based on. */
  readonly baseRev: number;
  /** "Save a version now": saves, then keeps a named version. */
  saveVersion(label: string): Promise<VersionMeta>;
  /**
   * After the plan was replaced on the server (a version was restored): forget unsaved changes and
   * continue from `rev`. The caller swaps in the new doc so `getDoc` returns it.
   */
  rebase(rev: number): Promise<void>;
  /** Hides the "you replaced your friend's changes" notice. */
  acknowledgeOverwrite(): void;
  /** Leaving the plan: saves, keeps a version if anything was edited, then stops. Never rejects. */
  close(): Promise<void>;
  /** Stops timers and listeners without saving (use `close()` normally). */
  dispose(): void;
}

/** What autosave talks to; replaceable in tests. */
export interface AutosaveDeps {
  save(planId: PlanId, doc: PlanDoc, baseRev: number, keepalive: boolean): Promise<SavePlanResponse>;
  createVersion(planId: PlanId, req: CreateVersionRequest, keepalive: boolean): Promise<VersionMeta>;
  uploadThumb(planId: PlanId, png: Blob): Promise<void>;
  local: LocalCopyStore;
  now(): number;
  /** Where to listen for `visibilitychange`, `pagehide` and `online`; null for none. */
  events: Pick<Window, 'addEventListener' | 'removeEventListener'> | null;
  /** Whether the page is hidden right now. */
  isHidden(): boolean;
}

export interface AutosaveOptions {
  /** Renders the 320 px card picture; called after a save, at most once a minute. */
  getThumbnail?: () => Promise<Blob | null>;
  deps?: Partial<AutosaveDeps>;
}

/** Wait after the last change before saving. */
export const DEBOUNCE_MS = 2000;
/** Active editing time between automatic versions. */
export const VERSION_EVERY_MS = 15 * 60 * 1000;
/** A gap between edits longer than this doesn't count as active editing. */
export const ACTIVE_GAP_MS = 60 * 1000;
/** Card pictures are uploaded at most this often. */
export const THUMB_EVERY_MS = 60 * 1000;
/** Local copy writes are coalesced over this long. */
const LOCAL_WRITE_MS = 300;
/** Keepalive requests are limited to 64 KB by browsers; bigger saves on close rely on the local copy. */
const KEEPALIVE_LIMIT = 60 * 1024;

/** Retry delay after `failures` failed saves in a row: 2, 4, 8, 16, 30, 30… seconds. */
export function retryDelay(failures: number): number {
  return Math.min(30_000, 2000 * 2 ** Math.max(0, failures - 1));
}

/**
 * Starts autosave for a plan. `getDoc` returns the current document (the editor's state);
 * `baseRev` is the rev it was loaded at (or the unsaved copy's `baseRev` after restoring one).
 */
export function createAutosave(
  planId: PlanId,
  getDoc: () => PlanDoc,
  baseRev: number,
  options: AutosaveOptions = {},
): Autosave {
  const deps: AutosaveDeps = {
    save: api.savePlan,
    createVersion: api.createVersion,
    uploadThumb: api.uploadThumb,
    local: options.deps?.local ?? defaultLocalStore(),
    now: () => Date.now(),
    events: typeof window === 'undefined' ? null : window,
    isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
    ...options.deps,
  };

  const status = createReadable<AutosaveStatus>({ state: 'saved', lastSavedAt: null, message: null, retryAt: null, overwrote: null });
  const patch = (p: Partial<AutosaveStatus>) => status.set({ ...status.get(), ...p });

  let rev = baseRev;
  let changeSeq = 0;
  let savedSeq = 0;
  let inFlight: Promise<void> | null = null;
  let failures = 0;
  let disposed = false;

  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let localTimer: ReturnType<typeof setTimeout> | null = null;
  let thumbTimer: ReturnType<typeof setTimeout> | null = null;

  // Auto versions
  let lastChangeAt = 0;
  let activeMs = 0;
  let editsSinceVersion = false;
  // Card picture
  let lastThumbAt = 0;
  let thumbStale = false;

  // Local copy writes and deletes run one after another so a late write can't undo a delete.
  let localQueue: Promise<void> = Promise.resolve();
  const queueLocal = (fn: () => Promise<void>) => {
    localQueue = localQueue.then(fn).catch(() => undefined);
    return localQueue;
  };

  const dirty = () => changeSeq !== savedSeq;
  const clear = (t: ReturnType<typeof setTimeout> | null) => {
    if (t) clearTimeout(t);
    return null;
  };

  function writeLocalNow(): Promise<void> {
    localTimer = clear(localTimer);
    if (!dirty()) return localQueue;
    const copy: LocalCopy = { planId, doc: getDoc(), baseRev: rev, editedAt: lastChangeAt || deps.now() };
    return queueLocal(() => deps.local.put(copy));
  }

  function scheduleSave(delay: number) {
    saveTimer = clear(saveTimer);
    if (disposed) return;
    saveTimer = setTimeout(() => {
      saveTimer = null;
      saveOnce(false).catch(() => undefined);
    }, delay);
  }

  function saveOnce(keepalive: boolean): Promise<void> {
    if (inFlight) {
      return inFlight.then(
        () => (dirty() ? saveOnce(keepalive) : undefined),
        () => (dirty() ? saveOnce(keepalive) : undefined),
      );
    }
    saveTimer = clear(saveTimer);
    retryTimer = clear(retryTimer);
    if (!dirty()) return Promise.resolve();

    const seq = changeSeq;
    const doc = getDoc();
    if (keepalive && JSON.stringify({ doc, baseRev: rev }).length > KEEPALIVE_LIMIT) keepalive = false;
    if (status.get().state !== 'offline') patch({ state: 'saving', message: null });

    inFlight = (async () => {
      try {
        const res = await deps.save(planId, doc, rev, keepalive);
        rev = res.rev;
        savedSeq = seq;
        failures = 0;
        const now = deps.now();
        patch({
          lastSavedAt: now,
          message: null,
          retryAt: null,
          state: dirty() ? 'pending' : 'saved',
          ...(res.overwrote ? { overwrote: { at: now } } : {}),
        });
        if (dirty()) scheduleSave(DEBOUNCE_MS);
        else {
          localTimer = clear(localTimer);
          void queueLocal(() => deps.local.delete(planId));
        }
        thumbStale = true;
        afterSave();
      } catch (e) {
        const f = e instanceof api.ApiFailure ? e : new api.ApiFailure('offline', api.friendlyMessage('offline'), 0);
        void writeLocalNow();
        if (f.retryable) {
          failures++;
          const delay = retryDelay(failures);
          patch({ state: 'offline', message: f.message, retryAt: deps.now() + delay });
          if (!disposed) {
            retryTimer = setTimeout(() => {
              retryTimer = null;
              saveOnce(false).catch(() => undefined);
            }, delay);
          }
        } else if (f.code === 'unauthorized') {
          patch({ state: 'signedOut', message: f.message, retryAt: null });
        } else {
          patch({ state: 'error', message: f.message, retryAt: null });
        }
        throw f;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  function afterSave() {
    if (disposed) return;
    if (activeMs >= VERSION_EVERY_MS) {
      activeMs = 0;
      editsSinceVersion = dirty();
      deps.createVersion(planId, { auto: true }, false).catch(() => {
        // Try again after the next save.
        activeMs = VERSION_EVERY_MS;
        editsSinceVersion = true;
      });
    }
    maybeThumb(false);
  }

  function maybeThumb(force: boolean) {
    const get = options.getThumbnail;
    if (!get || !thumbStale || disposed) return;
    const wait = lastThumbAt + THUMB_EVERY_MS - deps.now();
    if (!force && wait > 0) {
      thumbTimer ??= setTimeout(() => {
        thumbTimer = null;
        maybeThumb(false);
      }, wait);
      return;
    }
    thumbTimer = clear(thumbTimer);
    thumbStale = false;
    lastThumbAt = deps.now();
    get()
      .then((blob) => (blob ? deps.uploadThumb(planId, blob) : undefined))
      .catch(() => {
        thumbStale = true;
      });
  }

  function notifyChange() {
    if (disposed) return;
    const now = deps.now();
    if (lastChangeAt) activeMs += Math.min(now - lastChangeAt, ACTIVE_GAP_MS);
    lastChangeAt = now;
    changeSeq++;
    editsSinceVersion = true;
    localTimer ??= setTimeout(() => void writeLocalNow(), LOCAL_WRITE_MS);
    const st = status.get().state;
    if (st === 'offline' && retryTimer) return; // the retry will pick it up
    if (st !== 'saving') patch({ state: 'pending' });
    scheduleSave(DEBOUNCE_MS);
  }

  async function flush(): Promise<void> {
    if (inFlight) await inFlight.catch(() => undefined);
    if (dirty()) await saveOnce(false);
  }

  // ---- Page events ----
  const onVisibility = () => {
    if (deps.isHidden() && dirty()) {
      void writeLocalNow();
      saveOnce(true).catch(() => undefined);
    }
  };
  const onPageHide = () => {
    if (dirty()) {
      void writeLocalNow();
      saveOnce(true).catch(() => undefined);
    }
    if (editsSinceVersion) {
      editsSinceVersion = false;
      deps.createVersion(planId, { auto: true }, true).catch(() => undefined);
    }
  };
  const onOnline = () => {
    if (dirty() && status.get().state === 'offline') saveOnce(false).catch(() => undefined);
  };
  deps.events?.addEventListener('visibilitychange', onVisibility);
  deps.events?.addEventListener('pagehide', onPageHide);
  deps.events?.addEventListener('online', onOnline);

  function dispose() {
    disposed = true;
    saveTimer = clear(saveTimer);
    retryTimer = clear(retryTimer);
    thumbTimer = clear(thumbTimer);
    deps.events?.removeEventListener('visibilitychange', onVisibility);
    deps.events?.removeEventListener('pagehide', onPageHide);
    deps.events?.removeEventListener('online', onOnline);
  }

  return {
    notifyChange,
    flush,
    status,
    get baseRev() {
      return rev;
    },
    async saveVersion(label: string) {
      await flush();
      const v = await deps.createVersion(planId, { label: label.trim() || undefined }, false);
      activeMs = 0;
      editsSinceVersion = dirty();
      return v;
    },
    async rebase(newRev: number) {
      if (inFlight) await inFlight.catch(() => undefined);
      saveTimer = clear(saveTimer);
      retryTimer = clear(retryTimer);
      localTimer = clear(localTimer);
      rev = newRev;
      savedSeq = changeSeq;
      failures = 0;
      editsSinceVersion = false;
      activeMs = 0;
      thumbStale = true;
      patch({ state: 'saved', message: null, retryAt: null, lastSavedAt: deps.now() });
      await queueLocal(() => deps.local.delete(planId));
      maybeThumb(true);
    },
    acknowledgeOverwrite() {
      patch({ overwrote: null });
    },
    async close() {
      if (disposed) return;
      try {
        await flush();
        if (editsSinceVersion) {
          editsSinceVersion = false;
          await deps.createVersion(planId, { auto: true }, true);
        }
        maybeThumb(true);
      } catch {
        // The local copy keeps unsaved changes; the next open offers to restore them.
        await writeLocalNow();
      } finally {
        dispose();
      }
    },
    dispose,
  };
}

/** React hook: the live status of an autosave. */
export function useAutosaveStatus(autosave: Autosave): AutosaveStatus {
  return useSyncExternalStore(autosave.status.subscribe, autosave.status.get, autosave.status.get);
}
